/**
 * Trajectory Goal Types
 *
 * Defines types for motion-based spatial goals that track
 * landmark movement over time.
 */

// =============================================================================
// Enums
// =============================================================================

export type TrajectoryMode =
  | 'direction_only'      // Just check direction of movement
  | 'body_relative'       // Threshold in body-segment units
  | 'template_relative';  // Compare to template movement

export type DirectionType =
  | 'forward'    // In facing direction
  | 'backward'   // Opposite facing direction
  | 'up'         // Vertical upward
  | 'down'       // Vertical downward
  | 'left'       // Body-relative left
  | 'right'      // Body-relative right
  | 'apart'      // Away from body center
  | 'together';  // Toward body center

export type ReferenceType =
  | 'phase_start'       // First frame of current phase
  | 'phase_end'         // Last frame of current phase
  | 'annotation_start'  // First frame of annotation's active range
  | 'specific_frame';   // User-specified frame

export type AggregationType =
  | 'max_displacement'    // Maximum distance from reference
  | 'final_displacement'  // Distance at end of range
  | 'total_distance'      // Cumulative path length
  | 'min_displacement';   // Minimum distance reached

export type BodySegmentUnit =
  | 'shoulder_width'
  | 'hip_width'
  | 'torso_height'
  | 'arm_length'
  | 'forearm_length'
  | 'upper_arm_length'
  | 'leg_length'
  | 'thigh_length'
  | 'shin_length'
  | 'hand_length'
  | 'hand_span'
  | 'foot_length'
  | 'stride_width'
  | 'elbow_width'
  | 'wrist_width';

export type ComparisonOperator =
  | 'at_least'   // >=
  | 'at_most'    // <=
  | 'exactly'    // == with tolerance
  | 'maximize'   // Optimize for max
  | 'minimize';  // Optimize for min

export type TemplateComparisonOperator =
  | 'same_as'    // Within tolerance (e.g., 100% ±15%)
  | 'at_least'   // >= target (e.g., >= 80% of template)
  | 'at_most';   // <= target (e.g., <= 120% of template)

export type SpeedComparisonOperator =
  | 'same_as'          // Within tolerance (e.g., ±15%)
  | 'faster_or_equal'  // >= template speed
  | 'slower_or_equal'; // <= template speed

// -----------------------------------------------------------------------------
// Travel-distance target (travel-distance plan v2.1 §5.1, PR-A).
//
// Field names below are BYTE-IDENTICAL to
// `backend/app/models/spatial_goals.py::TravelTarget` and
// `docs/coach_input/TRAVEL_PR_A_CONTRACT.md` §1 — do not rename on either
// side without updating both.
// -----------------------------------------------------------------------------

/** Which of the coach's methods set the bar. Advisory/observability only —
 * the backend derives the actual resolution from which fields are set, not
 * from this label (contract §1.1). The panel still writes it so a reopened
 * goal can restore which control was selected. */
export type TravelTargetSource = 'demo' | 'demo_multiple' | 'coach';

/** 'at_least': overshoot is free (a reach). 'about': both over- and
 * under-shoot cost. 'at_most' is deferred (contract §1). */
export type TravelComparisonOperator = 'at_least' | 'about';

/** Stamped at authoring time so a later default-definition change cannot
 * silently re-score an existing goal. `null`/absent lets the backend cascade
 * composed_stack -> nose_to_lowest_foot per clip. Never rendered to the
 * coach as this raw string — see travelDistanceCopy.ts. */
export type BodyHeightDefinition = 'composed_stack' | 'nose_to_lowest_foot';

/**
 * How far a point should travel, as a share of how tall the person looks in
 * their own video. All fields optional (a half-filled draft persists); the
 * one save-time rule enforced by the backend is that `value_body_heights`
 * and `demo_multiple` cannot both be set (contract §1).
 */
export interface TravelTarget {
  target_source?: TravelTargetSource | null;
  /** The coach's typed absolute target, in shares of body height. > 0. */
  value_body_heights?: number | null;
  /** "a fifth further" = 1.2. Not exposed in the PR-A panel UI (owner's
   * flow is a strict A/B choice) but preserved on round-trip if present. */
  demo_multiple?: number | null;
  comparison?: TravelComparisonOperator;
  /** The Gaussian sigma the coach typed, if any. > 0. Always raised to the
   * enforced floor at score time — see travelDistanceCopy.ts. */
  tolerance_body_heights?: number | null;
  body_height_definition?: BodyHeightDefinition | null;
  /** The demo's measured ratio, recorded for display continuity. Never read
   * back as a scoring input — the scorer always re-measures fresh. */
  demo_value_body_heights?: number | null;
}

// -----------------------------------------------------------------------------
// Speed target (Goal Score PR-4). Byte-identical to
// `backend/app/models/spatial_goals.py::SpeedTarget` — sibling of
// TravelTarget, same authoring idiom (coach-typed absolute value OR a
// multiple of the demo, one-of), lives on `TrajectoryConfig.speed_target`
// (sibling field of `travel_target`, same movement_goal carrier).
// -----------------------------------------------------------------------------

/** 'at_least': overshoot (faster) is free, eligible for the Goal bonus.
 * 'about' is accepted by the model for forward compatibility but is not
 * yet scored (PR-4 only scores 'at_least'). */
export type SpeedComparisonMode = 'at_least' | 'about';

export interface SpeedTarget {
  target_source?: TravelTargetSource | null;
  /** The coach's typed absolute target, in body-heights per second. > 0. */
  value_body_heights_per_s?: number | null;
  /** "20% faster than my demo" = 1.2. */
  demo_multiple?: number | null;
  comparison?: SpeedComparisonMode;
  /** The Gaussian sigma, body-heights per second. */
  tolerance_body_heights_per_s?: number | null;
  body_height_definition?: BodyHeightDefinition | null;
  /** The demo's measured mean speed, for display continuity only. */
  demo_value_body_heights_per_s?: number | null;
}

// -----------------------------------------------------------------------------
// Angle target (Goal Score PR-5). Byte-identical to
// `backend/app/models/spatial_goals.py::AngleTarget` — carried on BOTH
// `JointAngleConfig.angle_goal_target` and `AxisConfig.angle_goal_target`
// (Angle's two carriers), never nested under `movement_goal`.
// -----------------------------------------------------------------------------

/** `about` never contributes a bonus. `at_least`/`at_most` are Goal-bonus-
 * eligible — but an `at_most` Angle Goal is ONLY the depth/range reading
 * ("squat as deep as possible"); a safety ceiling must be authored as a
 * plain Angle Check with `is_gate`, never as a Goal — see
 * `goalAuthoringCopy.ts`'s depth-vs-safety-fork strings. */
export type AngleGoalComparison = 'at_least' | 'at_most' | 'about';

export interface AngleTarget {
  target_source?: TravelTargetSource | null;
  /** The coach's typed absolute target, in degrees. Unconstrained sign. */
  value_degrees?: number | null;
  /** "10% more than my demo" = 1.1. */
  demo_multiple?: number | null;
  /** The Gaussian sigma, in degrees. */
  tolerance_degrees?: number | null;
  /** The demo's measured p95 angle, for display continuity only. */
  demo_value_degrees?: number | null;
}

// =============================================================================
// Configuration Interfaces
// =============================================================================

export interface DirectionConfig {
  direction: DirectionType;
  use_body_orientation: boolean;
  custom_vector?: [number, number, number];
  direction_threshold: number;  // 0-1
}

export interface BodyRelativeThreshold {
  value: number;
  unit: BodySegmentUnit;
  use_dominant_side: boolean;
}

export interface TemplateRelativeThreshold {
  // New comparison operator (preferred)
  comparison?: TemplateComparisonOperator;
  target_ratio?: number;  // Default 1.0 (100% of template)
  tolerance_percent: number;

  // Legacy fields (deprecated, kept for backward compatibility)
  minimum_ratio?: number;
  maximum_ratio?: number;
}

export interface SpeedThreshold {
  enabled: boolean;
  comparison: SpeedComparisonOperator;
  tolerance_percent: number;  // Default 15.0 (±15%)
}

export interface TrajectoryConfig {
  enabled: boolean;
  mode: TrajectoryMode;

  // Landmark to track
  tracked_landmark?: string;

  // Reference point
  reference_type: ReferenceType;
  reference_frame?: number;

  // Aggregation
  aggregation: AggregationType;

  // Direction (optional)
  direction?: DirectionConfig;

  // Thresholds (both can be defined for combined evaluation)
  body_threshold?: BodyRelativeThreshold;
  template_threshold?: TemplateRelativeThreshold;

  // Travel-distance target (contract §1) — mutually exclusive with every
  // other mode/threshold field in practice. When present, the evaluator
  // takes an early, dedicated path (max_displacement only, phase-scoped via
  // the annotation's existing target_phase field) and none of
  // mode/aggregation/direction/template_threshold/speed_threshold are
  // consulted. The authoring UI must never also set `is_gate` alongside
  // this (backend 422s a travel target paired with a gate).
  travel_target?: TravelTarget;

  // Speed Goal target (Goal Score PR-4) — sibling of travel_target, same
  // early-dedicated-path rule, mutually exclusive with it in practice.
  // NOT the same field as `speed_threshold` below (that is the pre-existing
  // template-relative "faster/slower than reference" check).
  speed_target?: SpeedTarget;

  // Speed threshold (independent of distance thresholds)
  speed_threshold?: SpeedThreshold;

  // When both thresholds are defined:
  // - true (default): pass if EITHER threshold is met (OR logic)
  // - false: pass only if BOTH thresholds are met (AND logic)
  use_or_logic?: boolean;

  // Comparison (for body-relative threshold)
  comparison: ComparisonOperator;

  // Gate condition: if true, failing this goal = automatic 0 for evaluation
  is_gate?: boolean;
}

// =============================================================================
// Result Interface
// =============================================================================

export interface TrajectoryGoalResult {
  enabled: boolean;
  passed: boolean;
  mode: TrajectoryMode;

  reference_frame: number;
  reference_position: [number, number, number];

  template_displacement?: number;
  athlete_displacement?: number;

  body_segment_length?: number;
  displacement_in_body_units?: number;

  displacement_ratio?: number;

  // Individual threshold results (for combined evaluation)
  body_threshold_passed?: boolean;
  body_threshold_explanation?: string;
  template_threshold_passed?: boolean;
  template_threshold_explanation?: string;

  // Speed threshold results
  template_speed?: number;
  athlete_speed?: number;
  speed_ratio?: number;
  speed_threshold_passed?: boolean;
  speed_threshold_explanation?: string;

  evaluation_logic?: 'single' | 'or' | 'and';

  direction_compliance?: number;
  primary_direction?: string;

  confidence: number;
  valid_frames: number;
  total_frames: number;

  explanation: string;

  trajectory_points?: Array<[number, number, number, number]>;  // [frame, x, y, z]
}

// =============================================================================
// UI Helper Types
// =============================================================================

export const BODY_SEGMENT_LABELS: Record<BodySegmentUnit, string> = {
  shoulder_width: 'Shoulder Width',
  hip_width: 'Hip Width',
  torso_height: 'Torso Height',
  arm_length: 'Arm Length',
  forearm_length: 'Forearm Length',
  upper_arm_length: 'Upper Arm Length',
  leg_length: 'Leg Length',
  thigh_length: 'Thigh Length',
  shin_length: 'Shin Length',
  hand_length: 'Hand Length',
  hand_span: 'Hand Span',
  foot_length: 'Foot Length',
  stride_width: 'Stride Width',
  elbow_width: 'Elbow Width',
  wrist_width: 'Wrist Width',
};

export const DIRECTION_LABELS: Record<DirectionType, string> = {
  forward: 'Forward',
  backward: 'Backward',
  up: 'Upward',
  down: 'Downward',
  left: 'Left',
  right: 'Right',
  apart: 'Apart (away from body)',
  together: 'Together (toward body)',
};

export const MODE_DESCRIPTIONS: Record<TrajectoryMode, string> = {
  direction_only: 'Check that the landmark moves in a specific direction',
  body_relative: 'Measure movement distance in body-relative units (e.g., shoulder-widths)',
  template_relative: 'Compare movement to the template\'s movement',
};

export const TEMPLATE_COMPARISON_LABELS: Record<TemplateComparisonOperator, string> = {
  same_as: 'Same As',
  at_least: 'At Least',
  at_most: 'At Most',
};

export const TEMPLATE_COMPARISON_DESCRIPTIONS: Record<TemplateComparisonOperator, string> = {
  same_as: 'Move the same distance as the template (within tolerance)',
  at_least: 'Move at least this percentage of the template distance',
  at_most: 'Move at most this percentage of the template distance',
};

export const SPEED_COMPARISON_LABELS: Record<SpeedComparisonOperator, string> = {
  same_as: 'About Same Speed',
  faster_or_equal: 'Faster or Equal',
  slower_or_equal: 'Slower or Equal',
};

export const SPEED_COMPARISON_DESCRIPTIONS: Record<SpeedComparisonOperator, string> = {
  same_as: 'Move at approximately the same speed as the template',
  faster_or_equal: 'Move at least as fast as the template',
  slower_or_equal: 'Move no faster than the template',
};

// Default speed threshold configuration
export const DEFAULT_SPEED_THRESHOLD: SpeedThreshold = {
  enabled: false,
  comparison: 'same_as',
  tolerance_percent: 15.0,
};
